// tynk-pixel v10.1 (tasks.tynk.ai): o módulo comum de rastreamento dos apps *.tynk.ai, com UM adaptador próprio. Este
// app é o AFFiNE e NÃO carrega clerk-js: a identidade vem da sessão do AFFiNE (GET /api/auth/session) mais a claim `ent`
// da Clerk Frontend API (GET /v1/client). Por isso o arquivo não é cópia do módulo comum: as regras são as mesmas e as
// diferenças estão marcadas com «adaptador». O PageView só sai com a lib carregada E destravada, reconferindo tudo de
// forma síncrona antes de enviar.
//
// Decisões do Jeff 26/10:
//  - Auth lenta/não resolvida: NUNCA cai para visitor/lead por timeout — indisponível = pula o evento (a próxima
//    navegação reenvia). Resultado assíncrono (sessão, claim, SHA-256) de uma navegação superada é descartado pelo
//    guard de navId, e a identidade é reconferida bem na hora do envio → nunca vaza `em`/`external_id` de outra conta.
//  - Troca de conta / re-login SEM reload: o pixel REINICIA com a nova identidade (zera o userData da conta anterior
//    no SDK e re-inicializa com a nova — nota-sdk.md) em vez de ficar parado até recarregar. Logout → segue como
//    visitante sem nenhum `em`/`external_id` da conta anterior. A próxima navegação rastreável manda UM PageView com
//    a nova identidade; na mesma URL, o dedupe (por rota + identidade) ainda deixa sair esse PageView uma vez.
//
// v10 e v10.1 (NEG-38 e NEG-39, etapa 1 da NEG-35; decisões do Jeff de 01/11/2026):
//  - As telas de ENTRADA passam a ser rastreadas: visitante manda PageView `visitor`. Isso REVOGA a regra de 26/10
//    («rotas de auth nunca rastreadas»). Logado em tela de entrada não gera PageView.
//  - A URL é limpa ANTES de qualquer envio (`limparUrl`, a mesma régua do módulo comum), na carga e em cada troca de
//    rota: o pushState e o replaceState gravam a URL nova já limpa. O que o app precisa ler fica de fora: as rotas de
//    `ROTAS_FORA` não têm a URL tocada, e o parâmetro preso (`paramPreso`) fica na URL e trava o envio.
//  - `user_type` ganha `internal` (lista de e-mails, provisória até a etapa 3) e perde `buyer`.
//  - Cada PageView leva um `eventID` (UUID) próprio; o MESMO id vai ao dataLayer como
//    `{ event: 'page_view', event_id, user_type }`.
//  - O GTM-574XSC8T (GA4 G-Y9L3S6E6RB) carrega pelo `/tk` do próprio host, uma vez, pela mesma porta do pixel. Sem gtag
//    direto. Nenhum Purchase no navegador: o módulo não tem caminho para isso.
import { clerkFapiUrl } from '@affine/core/components/sign-in/clerk-dev-browser';

export const PIXEL_ID = '556956583249491';
const FBEVENTS = 'https://connect.facebook.net/en_US/fbevents.js';
const HOSTS_PADRAO = ['tasks.tynk.ai'] as const;
const TIMEOUT_MS = 4000; // orçamento para resolver a identidade (sessão + claim)
const LIMITE_PRIVADA_MS = 15000; // teto p/ a WorkspacePage confirmar /workspace (não é autorização)
const LIMITE_DESTRAVADO_MS = 8000; // teto p/ o fbevents destravar (config/plugins)
const MS_POR_DIA = 86_400_000;

// Loader do GTM-574XSC8T pelo `/tk` do host. A query é pública e a mesma em todos os domínios: é a `QUERY_DO_LOADER` de
// `apps/aulas-next/lib/conversao/gtm.ts` (jeffecom). A rota `/tk*` de cada host é do jeffecom (ponte-stape).
export const GTM_LOADER_PATH = '/tk/afnzukbxvj.js';
export const GTM_LOADER_QUERY =
  '7ifu0qrp=AxJFJT84V1tdQiAxKE0uSBxLSkxaUx4AVxwdBA0QGQEHWBENTQYVHldZDAk%3D';

export type UserType = 'visitor' | 'lead' | 'member' | 'internal';

// Equipe e QA → `internal` (decisão do Jeff, 01/11/2026). REGRA PROVISÓRIA: sai na etapa 3 da NEG-35, quando o cofre de
// identidade existir. Único lugar da regra: a lista OPERADORES do acesso-worker, mais qualquer e-mail do domínio
// @grupoecom.io (que já cobre o jeff+qa@grupoecom.io). Compara em minúsculas. `role` e `publicMetadata` NÃO decidem.
const OPERADORES_INTERNOS: ReadonlySet<string> = new Set([
  'jeff@grupoecom.io',
  'admin@jeffecom.com',
  'alex@grupoecom.io',
  'julio@grupoecom.io',
  'jeff+qa@grupoecom.io',
]);
const DOMINIO_INTERNO = '@grupoecom.io';

/** O e-mail é da equipe ou de QA? (provisório até a etapa 3; ver `OPERADORES_INTERNOS`). */
export function contaInterna(email: string | null | undefined): boolean {
  const e = (email ?? '').trim().toLowerCase();
  return !!e && (OPERADORES_INTERNOS.has(e) || e.endsWith(DOMINIO_INTERNO));
}

// Códigos da política (acesso-worker/politica.json + `co`). Fora desta lista ninguém fica ativo (igual ao gate).
const CODIGOS_VALIDOS: ReadonlySet<string> = new Set(
  'gn sp ty cr sl pg pl vo mi ii au su ta ap ch tk co'.split(' ')
);

// Rotas NUNCA rastreadas, e com a limpeza da URL DESLIGADA: a tela lê da query o que o módulo tiraria (minúsculas,
// decode por segmento, casa o segmento inteiro). A primeira linha é a do módulo comum: callbacks, proxy e saída.
const ROTAS_AUTH: readonly string[] = [
  '/auth',
  '/sso-callback',
  '/oauth',
  '/verify',
  '/magic-link',
  '/reset-password',
  '/forgot-password',
  '/clerk-proxy',
  '/sign-out',
  '/signout',
  '/logout',
  '/sair',
];
// Adaptador: as rotas do roteador do AFFiNE (`desktop/router.tsx`) que também ficam fora.
const ROTAS_DO_AFFINE: readonly string[] = [
  '/desktop-signin', // oauth-login.tsx: provider, client, redirect_uri, __clerk_db_jwt
  '/open-app', // `url` traz o deep link com o payload do login; `signin-redirect` monta um link com o token de sessão
  '/redirect-proxy', // redirect_uri é lido no loader, e o destino é qualquer URL
  '/invite', // o id no caminho é a chave do convite
  '/share', // redireciona para o documento compartilhado
  '/template', // name, mode e snapshotUrl, lidos no loader
  '/clipper',
  '/theme-editor',
  '/expired',
  '/upgrade-success', // `info` traz conta, e-mail e nome; `session_id` gera a chave de licença
  '/ai-upgrade-success', // `info`, como acima
  '/admin', // dado de cliente na URL
];
const ROTAS_FORA: readonly string[] = [...ROTAS_AUTH, ...ROTAS_DO_AFFINE];
// Telas de entrada (v10): rastreadas só para visitante; o referrer que vem delas é cortado na origem.
const ROTAS_ENTRADA: readonly string[] = [
  '/sign-in',
  '/sign-up',
  '/signin',
  '/signup',
  '/login',
  '/entrar',
  '/cadastro',
  '/register',
];
// Etapa de callback dentro de uma tela de entrada: fica fora, como os callbacks na raiz.
const SEGMENTO_DE_CALLBACK =
  /^(sso|oauth|verify|magic-link|reset-password|forgot-password)|callback/;

// Chaves de query/hash que não podem virar PageView. As três últimas são do tasks: ele já as barrava, e o `/auth` não é
// a única tela que poderia recebê-las. `q`/`search`/`busca` só pegam quando o valor tem "@".
const CHAVES_SENSIVEIS: ReadonlySet<string> = new Set([
  '__clerk_ticket',
  '__clerk_status',
  '__clerk_db_jwt',
  'ticket',
  'token',
  'code',
  'state',
  'email',
  'e-mail',
  'otp',
  'password',
  'senha',
  'secret',
  'key',
  'session',
  'invite',
  'auth',
  'authorization',
  'userid',
  'user_id',
  'inviteid',
]);
// v10.1: pedaços de nome de chave que indicam segredo, em qualquer posição. `session`, `auth`, `ticket` e `otp` ficam
// só na lista exata acima: como pedaço, pegariam `session_id`, `author` e `ticket_id`, que o app pode precisar ler.
const PEDACOS_SENSIVEIS = /token|secret|passw|senha|jwt|api[_-]?key|handshake/;
// v10.1: chaves de campanha e de clique, além de toda `utm_*`: o valor é número longo, não telefone.
const CHAVES_DE_CAMPANHA: ReadonlySet<string> = new Set([
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'ttclid',
  'campaign_id',
  'adset_id',
  'ad_id',
  'test_event_code',
  'gad_campaignid',
  'campaignid',
  'adgroupid',
  'creative',
  'hsa_cam',
  'hsa_grp',
  'hsa_ad',
]);
// Adaptador: chave do AFFiNE com número longo que não é telefone. O `/subscribe` remonta o destino do login a partir de
// `location.search`, e um cupom só com dígitos sumiria depois do login. As outras chaves de `/subscribe` e `/upgrade-to-team`
// são listas fechadas com letras, e as de `/upgrade-success*` estão em rota fora.
const CHAVES_NUMERICAS_DO_AFFINE: ReadonlySet<string> = new Set(['coupon']);
// v10.1: valor com cara de telefone: só dígitos e separadores, de 10 a 15 dígitos.
function pareceTelefone(valor: string): boolean {
  if (!/^\+?[\d\s().-]+$/.test(valor)) return false;
  const digitos = valor.replace(/\D/g, '').length;
  return digitos >= 10 && digitos <= 15;
}

// --- funções puras ------------------------------------------------------------------------------------------

/** Guarda de host/janela: host exato + topo da janela (o Clerk/OIDC pode abrir iframes). */
export function podeAtivar(p: {
  hostname: string;
  isTop: boolean;
  hosts: readonly string[];
}): boolean {
  return p.hosts.includes(p.hostname) && p.isTop;
}

/** A claim `ent` tem pelo menos uma ferramenta vigente? Mesma regra do `usuario.ativo` do gate. */
export function assinaturaAtiva(
  ent: unknown,
  hoje = Math.floor(Date.now() / MS_POR_DIA)
): boolean {
  if (!ent || typeof ent !== 'object') return false;
  const c = ent as { v?: unknown; t?: unknown };
  if (c.v !== 1 || !c.t || typeof c.t !== 'object') return false;
  return Object.entries(c.t as Record<string, unknown>).some(
    ([cod, v]) =>
      CODIGOS_VALIDOS.has(cod) &&
      (v === 'p' || (typeof v === 'number' && v >= hoje))
  );
}

/** Etiqueta: sem login = visitor; equipe/QA = internal; logado = member (assinatura ativa) ou lead (fallback seguro). */
export function tipoDoUsuario(p: {
  logado: boolean;
  ent?: unknown;
  email?: string | null;
}): UserType {
  if (!p.logado) return 'visitor';
  if (contaInterna(p.email)) return 'internal';
  return assinaturaAtiva(p.ent) ? 'member' : 'lead';
}

async function sha256Hex(v: string): Promise<string> {
  const d = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(v.trim().toLowerCase())
  );
  return Array.from(new Uint8Array(d), b =>
    b.toString(16).padStart(2, '0')
  ).join('');
}

/** Normaliza o pathname como o React Router (decode por segmento, barras repetidas viram uma, minúsculas); malformado → null. */
export function normalizarCaminho(pathname: string): string | null {
  try {
    return pathname
      .split('/')
      .map(s => decodeURIComponent(s))
      .join('/')
      .replace(/\/{2,}/g, '/')
      .toLowerCase();
  } catch {
    return null;
  }
}

const casaPrefixo = (p: string, lista: readonly string[]): boolean =>
  lista.some(r => p === r || p.startsWith(r + '/'));

/** A rota fica fora (nem rastreada, nem com a URL tocada)? Caminho malformado → fora (fail closed). */
export function rotaDeveSerIgnorada(path: string): boolean {
  const p = normalizarCaminho(path);
  if (p === null) return true;
  if (casaPrefixo(p, ROTAS_FORA)) return true;
  return (
    casaPrefixo(p, ROTAS_ENTRADA) &&
    p
      .split('/')
      .slice(2)
      .some(s => SEGMENTO_DE_CALLBACK.test(s))
  );
}

/** Tela de entrada (login, cadastro…)? Só conta visitante; o referrer que vem dela é cortado. Decode quebrado conta como entrada. */
function rotaDeEntrada(path: string): boolean {
  const p = normalizarCaminho(path);
  return p === null || casaPrefixo(p, ROTAS_ENTRADA);
}

/** Algum par chave=valor da query (ou do hash) passa no `teste`? O hash pode ser `#a=b` ou `#/rota?a=b`: lê os dois
 * jeitos. Erro de leitura e %-inválido contam como achado (o chamador pula). */
function algumParam(
  bruto: string,
  teste: (k: string, v: string) => boolean
): boolean {
  const y = bruto.replace(/^[?#]/, '');
  if (!y) return false;
  const i = y.indexOf('?');
  let achou = false;
  try {
    if (y.includes('%')) decodeURIComponent(y.replace(/\+/g, '%20')); // sonda: lança em %-inválido
    for (const parte of i >= 0 ? [y, y.slice(i + 1)] : [y])
      new URLSearchParams(parte).forEach((v, k) => {
        if (teste(k, v)) achou = true;
      });
  } catch {
    return true;
  }
  return achou;
}

/**
 * Parâmetro que FICA na URL, mas trava a carga e o envio enquanto existir:
 *  - o do Clerk: a chave começa com `__clerk`, ou o valor carrega um `__clerk`;
 *  - um endereço com dado sensível dentro: `redirect_uri=/x?token=…` ou com e-mail. Apagá-lo quebraria o
 *    redirecionamento depois do login;
 *  - adaptador: um endereço DESTE app que aponta para uma rota fora. O `/invite` manda para o login com
 *    `redirect_uri=/invite/<id>`, e o `dl` do Meta levaria a chave do convite. Em outro host o caminho não é nosso: um
 *    `utm_content=https://site/share/x` não trava nada.
 *
 * Endereço dentro de endereço para de ser lido em `MAX_ANINHAMENTO` e trava (fail closed). Sem o limite, o tempo dobrava
 * a cada 2 níveis: um link de 140 caracteres (`?x=/?x=/?x=…`) congelava a montagem por 28 s. A leitura da rota fora olha
 * só o caminho deste nível, sem recursão, e fica depois do limite.
 */
const MAX_ANINHAMENTO = 4;
function paramPreso(chave: string, valor: string, nivel = 0): boolean {
  if (
    chave.toLowerCase().startsWith('__clerk') ||
    valor.toLowerCase().includes('__clerk')
  )
    return true;
  if (!/^(\/|https?:)/i.test(valor)) return false; // não é endereço
  if (valor.includes('@')) return true;
  if (nivel >= MAX_ANINHAMENTO) return true;
  try {
    const base = 'https://endereco.invalid';
    const u = new URL(valor, base);
    const desteApp = u.origin === base || u.hostname === location.hostname;
    const dentro = (k: string, v: string): boolean =>
      paramSensivel(k, v, nivel + 1);
    return (
      (desteApp && rotaDeveSerIgnorada(u.pathname)) ||
      algumParam(u.search, dentro) ||
      algumParam(u.hash, dentro)
    );
  } catch {
    return true; // endereço ilegível: trava (fail closed)
  }
}

/** Parâmetro sensível: os presos acima, as chaves da lista, todo valor com "@", chave com cara de segredo e valor com
 * cara de telefone fora das chaves de campanha. */
function paramSensivel(chave: string, valor: string, nivel = 0): boolean {
  const k = chave.trim().toLowerCase();
  if (
    valor.includes('@') ||
    CHAVES_SENSIVEIS.has(k) ||
    PEDACOS_SENSIVEIS.test(k)
  )
    return true;
  if (
    !k.startsWith('utm_') &&
    !CHAVES_DE_CAMPANHA.has(k) &&
    !CHAVES_NUMERICAS_DO_AFFINE.has(k) &&
    pareceTelefone(valor)
  )
    return true;
  return paramPreso(chave, valor, nivel);
}

/**
 * A URL sem as chaves sensíveis que podem sair (e-mail, token, senha, código…), e sem o hash se ele tiver alguma; `null`
 * se não há o que tirar. As presas (`paramPreso`) ficam: com elas na URL, `temParametroSensivel` segue verdadeiro e nada
 * sai. Devolve caminho + query + hash. Mexe no `u` recebido.
 */
function semDadoSensivel(u: URL): string | null {
  const tirar = new Set<string>();
  const presas = new Set<string>();
  u.searchParams.forEach((v, k) => {
    if (paramPreso(k, v)) presas.add(k);
    else if (paramSensivel(k, v)) tirar.add(k);
  });
  presas.forEach(k => tirar.delete(k)); // chave repetida com um valor preso: fica inteira (e trava)
  const hashSujo =
    algumParam(u.hash, paramSensivel) && !algumParam(u.hash, paramPreso);
  if (!tirar.size && !hashSujo) return null;
  tirar.forEach(k => u.searchParams.delete(k));
  if (hashSujo) u.hash = '';
  return u.pathname + u.search + u.hash;
}

/** Limpa a URL ATUAL por `replaceState`, sem recarregar. Usa o `replaceState` ORIGINAL, para não disparar uma medição a
 * mais, e preserva o `history.state` (é onde o React Router guarda a posição dele). */
function limparUrl(replaceOriginal: History['replaceState']): void {
  try {
    const limpa = semDadoSensivel(new URL(location.href));
    if (limpa !== null) replaceOriginal(history.state, '', limpa);
  } catch {
    // URL estranha: não mexe; `temParametroSensivel` decide (e um erro de leitura pula o envio)
  }
}

/** Tem parâmetro sensível no search/hash (ou deu ruim ao ler)? Então não carrega nem rastreia. Quase sempre sobra só o
 * preso: o resto o `limparUrl` já tirou. */
export function temParametroSensivel(search: string, hash: string): boolean {
  return algumParam(search, paramSensivel) || algumParam(hash, paramSensivel);
}

/** UUID v4 do evento: o mesmo id vai no pixel e no dataLayer, para o GTM deduplicar com o servidor. */
function novoEventId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Sanea o `document.referrer` (o fbevents o manda como `rl` a cada evento). Vazio/malformado → ''; param sensível/`@`,
 * caminho pulado, tela de entrada ou documento da mesma origem → só a origem; limpo → origem + caminho, sem query/hash.
 */
export function sanitizarReferrer(
  referrer: string,
  hosts: readonly string[],
  skip: (p: string) => boolean
): string {
  if (!referrer) return '';
  let u: URL;
  try {
    u = new URL(referrer);
  } catch {
    return '';
  }
  const mesmaOrigem = hosts.includes(u.hostname);
  if (
    temParametroSensivel(u.search, u.hash) ||
    (mesmaOrigem &&
      (skip(u.pathname) ||
        rotaDeEntrada(u.pathname) ||
        normalizarCaminho(u.pathname)?.startsWith('/workspace/')))
  )
    return `${u.origin}/`;
  return u.origin + u.pathname;
}

// Shape do /v1/client (Clerk FAPI), snake_case.
type FapiUser = {
  email_addresses?: { id?: string; email_address?: string }[] | null;
  primary_email_address_id?: string | null;
  public_metadata?: Record<string, unknown> | null;
};
type FapiClient = {
  response?: {
    sessions?: { status?: string; user?: FapiUser | null }[] | null;
  } | null;
};

/** Claim `ent` da sessão ATIVA cujo e-mail primário bate com o do app (case-insensitive); senão undefined → lead. */
export function entDaSessaoFapi(client: unknown, emailApp: string): unknown {
  const alvo = emailApp.trim().toLowerCase();
  if (!alvo) return undefined;
  const sessions = (client as FapiClient | null)?.response?.sessions;
  if (!Array.isArray(sessions)) return undefined;
  for (const s of sessions) {
    const user = s?.user;
    if (s?.status !== 'active' || !user) continue;
    const primaria = user.email_addresses?.find(
      e => e.id === user.primary_email_address_id
    )?.email_address;
    if (primaria?.trim().toLowerCase() === alvo)
      return user.public_metadata?.ent;
  }
  return undefined;
}

/** Prefixo `/workspace/<id>` de um caminho JÁ normalizado; null se não for rota de workspace. */
export function prefixoWorkspace(pathNormalizado: string): string | null {
  const partes = pathNormalizado.split('/'); // ['', 'workspace', '<id>', ...]
  return partes[1] === 'workspace' && partes[2]
    ? `/workspace/${partes[2]}`
    : null;
}

// --- marcadores de página (/workspace/:ws/:doc: WorkspacePage=privada vs SharePage=pública) ------------------
// Público: contador global (qualquer tela pública suprime o rastreio — fail-safe, nunca sobre-rastreia).
// Privado: contador POR workspace (`/workspace/<id>`) — a autorização de um workspace NÃO vale para outro, então
// uma transição SPA de /workspace/A para um doc compartilhado de /workspace/B não herda o marcador privado de A
// (v7_startTransition deixa a URL virar antes da limpeza da tela antiga). Contadores aguentam StrictMode + telas
// aninhadas; os ouvintes deixam o pixel ESPERAR sem timer.
let marcadorPublico = 0;
const marcadoresPrivado = new Map<string, number>();
const ouvintes = new Set<() => void>();
function notificar(): void {
  // Iterar o Set direto é seguro: um ouvinte só remove a si mesmo (permitido no for…of de um Set).
  for (const cb of ouvintes) {
    try {
      cb();
    } catch {
      // um ouvinte com erro não derruba os outros
    }
  }
}
function limpeza(baixar: () => void): () => void {
  let limpo = false;
  return () => {
    if (limpo) return;
    limpo = true;
    baixar();
    notificar();
  };
}
/** Marca a tela como pública (não rastrear). Devolve a limpeza idempotente para o unmount. */
export const marcarPaginaPublica = (): (() => void) => {
  marcadorPublico++;
  notificar();
  return limpeza(() => {
    marcadorPublico = Math.max(0, marcadorPublico - 1);
  });
};
// Chave do marcador privado: `/workspace/<id>` normalizado (decode + minúsculas) — igual ao prefixo recomputado no
// envio. Com `workspaceId` explícito (o chamador conhece o workspace) não dependemos da URL global, que o
// v7_startTransition pode já ter virado; sem argumento, caímos de volta na inferência pela URL atual.
function chavePrivada(workspaceId?: string): string {
  const base =
    workspaceId !== undefined
      ? `/workspace/${workspaceId}`
      : typeof location !== 'undefined'
        ? location.pathname
        : '';
  const p = normalizarCaminho(base);
  return (p !== null ? prefixoWorkspace(p) : null) ?? p ?? '';
}
/**
 * Marca a tela como workspace de MEMBRO (autorização positiva), vinculada a `/workspace/<id>`. Passe o `workspaceId`
 * explícito: assim a troca A→B sem remontagem (o React reusa o componente) re-registra pelo ID certo, sem inferir da
 * URL global. Devolve a limpeza idempotente para o unmount.
 */
export const marcarPaginaPrivada = (workspaceId?: string): (() => void) => {
  const chave = chavePrivada(workspaceId);
  marcadoresPrivado.set(chave, (marcadoresPrivado.get(chave) ?? 0) + 1);
  notificar();
  return limpeza(() => {
    const n = (marcadoresPrivado.get(chave) ?? 0) - 1;
    if (n > 0) marcadoresPrivado.set(chave, n);
    else marcadoresPrivado.delete(chave);
  });
};
export const temPaginaPublica = (): boolean => marcadorPublico > 0;
/** Sem argumento: há QUALQUER workspace privado ativo. Com prefixo: há autorização para ESSE workspace. */
export const temPaginaPrivada = (prefixo?: string): boolean =>
  prefixo === undefined
    ? marcadoresPrivado.size > 0
    : (marcadoresPrivado.get(prefixo) ?? 0) > 0;

// Espera a confirmação positiva (privado do workspace ATUAL ativo e nenhum público) via ouvinte; o timer só desiste.
function aguardarPaginaPrivada(
  prefixo: string,
  limiteMs: number
): Promise<boolean> {
  const ok = (): boolean => temPaginaPrivada(prefixo) && !temPaginaPublica();
  if (ok()) return Promise.resolve(true);
  return new Promise(resolve => {
    let feito = false;
    const fim = (v: boolean): void => {
      if (feito) return;
      feito = true;
      clearTimeout(timer);
      ouvintes.delete(ouvinte);
      resolve(v);
    };
    const timer = setTimeout(() => fim(false), limiteMs);
    const ouvinte = (): void => {
      if (ok()) fim(true);
    };
    ouvintes.add(ouvinte);
  });
}

// --- sessão do AFFiNE + ent no Clerk -------------------------------------------------------------------------
// Três estados (a distinção é crítica): logado / deslogado (200 sem usuário) / indisponível (rede/timeout/não-2xx).
// Indisponível NÃO é logout: a navegação só pula o próprio evento, sem travar nem mudar identidade.
type Sessao =
  | { estado: 'logado'; email: string }
  | { estado: 'deslogado' }
  | { estado: 'indisponivel' };

async function buscarSessaoApp(signal: AbortSignal): Promise<Sessao> {
  try {
    const res = await fetch('/api/auth/session', {
      credentials: 'include',
      signal,
    });
    if (!res.ok) return { estado: 'indisponivel' };
    const data = (await res.json()) as { user?: { email?: unknown } | null };
    const email =
      typeof data?.user?.email === 'string'
        ? data.user.email.trim().toLowerCase()
        : undefined;
    if (!data?.user || !email) return { estado: 'deslogado' };
    return { estado: 'logado', email };
  } catch {
    return { estado: 'indisponivel' };
  }
}

// Resultado da claim. A distinção é CRÍTICA (decisão Jeff 26/10, achado 3): uma resposta 2xx válida sem assinatura é um
// `lead` legítimo e ENVIA (`ent` vem undefined); já uma consulta INDISPONÍVEL (rede/abort/timeout/não-2xx) NÃO vira
// `lead` — pula o evento daquela navegação (não baixa o script, não inicializa, não envia), como a sessão indisponível.
type EntResultado = { estado: 'ok'; ent: unknown } | { estado: 'indisponivel' };

/** Lê a claim `ent` no Clerk FAPI. Não-2xx/rede/abort/timeout → indisponível; 2xx → ok (`ent` pode ser undefined=lead). */
async function buscarEnt(
  emailApp: string,
  signal: AbortSignal
): Promise<EntResultado> {
  try {
    const res = await fetch(await clerkFapiUrl('/v1/client'), {
      credentials: 'include',
      signal,
    });
    if (!res.ok) return { estado: 'indisponivel' };
    return { estado: 'ok', ent: entDaSessaoFapi(await res.json(), emailApp) };
  } catch {
    return { estado: 'indisponivel' };
  }
}

// --- stub do fbevents ---------------------------------------------------------------------------------------
type FbLocks = {
  isLocked?: () => boolean;
  onUnlocked?: (cb: () => void) => void;
};
// Campos de advanced matching que o SDK guarda no objeto do pixel. `init` só grava userData quando o atual está
// VAZIO (ramo DUPLICATE_PIXEL_ID do fbevents): para reiniciar com outra conta sem reload, zeramos estes campos antes
// do novo `init`. Exportado para o teste contra o SDK real (FBEVENTS_PATH) usar a MESMA lista do runtime.
export const CAMPOS_USERDATA = [
  'userData',
  'alternateUserData',
  'userDataFormFields',
  'alternateUserDataFormFields',
  'censoredUserDataFormat',
  'censoredUserDataFormatFormFields',
  'userDataWebsiteVariables',
  'userDataAI',
] as const;
// Objeto de pixel na lista viva do SDK (`Ne`/`getState().pixels`), o MESMO guardado em `instance.pixelsByID`.
type PixelSdk = {
  id?: unknown;
  trackSingleOnly?: boolean;
} & Partial<Record<(typeof CAMPOS_USERDATA)[number], unknown>>;
type FbqStub = {
  (...a: unknown[]): void;
  callMethod?: (...a: unknown[]) => void;
  queue: unknown[];
  push: unknown;
  loaded: boolean;
  version: string;
  disablePushState: boolean;
  instance?: { locks?: FbLocks };
  // Lista viva de pixels do SDK (`Ne`): o MESMO objeto que o laço de disparo do global lê e que o SDK guarda em
  // `instance.pixelsByID`. Marcar `trackSingleOnly` aqui tira o pixel do PageView automático (history + bfcache).
  getState?: () => { pixels?: PixelSdk[] } | undefined;
};
type W = Window & {
  fbq?: FbqStub;
  _fbq?: unknown;
  __tynkPixel?: boolean;
  dataLayer?: Array<Record<string, unknown>>;
};

/** Empurra um evento no dataLayer do GTM (cria a fila se ainda não existe). */
function empurrar(w: W, evento: Record<string, unknown>): void {
  (w.dataLayer = w.dataLayer || []).push(evento);
}

/** Carrega o GTM pelo `/tk` do próprio host, sem gtag direto. Chamado uma vez, junto do fbevents: depois da identidade,
 * com a URL limpa e o referrer já saneado. */
function carregarGtm(w: W): void {
  empurrar(w, { 'gtm.start': Date.now(), event: 'gtm.js' });
  const t = document.createElement('script');
  t.async = true;
  t.src = `https://${location.host}${GTM_LOADER_PATH}?${GTM_LOADER_QUERY}`;
  document.head.append(t);
}

// Stub oficial + baixa o script (uma vez). Só chamado quando um PageView autorizado vai sair.
function stub(w: W): HTMLScriptElement | null {
  if (w.fbq) return null;
  const n = function (...a: unknown[]) {
    if (n.callMethod) n.callMethod(...a);
    else n.queue.push(arguments);
  } as FbqStub;
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  // (a) ANTES de o SDK carregar e de qualquer init (decisão do Jeff t49s691): o `setupSPANavigation` do fbevents lê
  // isto e NÃO injeta pushState/replaceState nem instala o popstate — barra o PageView automático de HISTORY (o Next
  // segue dono do history). O ramo de bfcache (pageshow persisted) NÃO é coberto por isto; para ele, o pixel 01 é
  // isolado com `trackSingleOnly` (ver isolarPixelDoAutomatico). Nunca usamos fbq('consent','revoke').
  n.disablePushState = true;
  const t = document.createElement('script');
  t.async = true;
  t.src = FBEVENTS;
  document.head.append(t);
  return t;
}

/** Espera o fbevents carregar de fato (onload → define fbq.callMethod). Resolve na hora se já carregou. */
function aguardarScriptPadrao(el: HTMLScriptElement | null): Promise<void> {
  if ((window as W).fbq?.callMethod) return Promise.resolve();
  return new Promise(resolve => {
    if (!el) return resolve();
    el.addEventListener('load', () => resolve(), { once: true });
  });
}

/**
 * (b) Tira o pixel 01 do caminho do PageView AUTOMÁTICO do fbevents.js (decisão do Jeff t49s691). O SDK dispara o
 * automático como comando GLOBAL — `callMethod.call(ev,'trackCustom','PageView')` — tanto na navegação por history
 * quanto na restauração por bfcache (o handler de `pageshow` com `persisted`, que o SDK instala SEM olhar o
 * `disablePushState`). O laço de disparo do global pula todo pixel com `trackSingleOnly===true`
 * (`…&&g.trackSingleOnly!==true` no fbevents), enquanto o NOSSO `trackSingle(PIXEL_ID,'PageView',…)` segue por
 * `trackSingleGeneric`, que mira o pixel pelo id e ignora a marca — continua saindo normalmente. Gravamos no objeto
 * do pixel direto na lista de `getState().pixels` (o MESMO objeto que o laço lê e que o SDK guarda em
 * `instance.pixelsByID`), porque o comando público `fbq('set','trackSingleOnly',…)` não aplica a marca nesta versão
 * do SDK. Não toca em evento de navegador nem no DOM: só o estado do próprio pixel no SDK. Idempotente; sem o SDK
 * carregado ainda, não faz nada (o `disablePushState` segue barrando o PageView automático de history até lá).
 */
function isolarPixelDoAutomatico(w: W): void {
  try {
    const pixels = w.fbq?.getState?.()?.pixels;
    if (!Array.isArray(pixels)) return;
    for (const p of pixels)
      if (p?.id != null && String(p.id) === PIXEL_ID) p.trackSingleOnly = true;
  } catch {
    // getState ausente/instável: o disablePushState ainda barra o PageView automático de history
  }
}

/**
 * Zera o advanced matching do pixel 01 no objeto vivo do SDK (decisão do Jeff 26/10). O `init` do fbevents só grava
 * userData novo quando o atual está VAZIO (ramo DUPLICATE_PIXEL_ID); sem isto, uma troca de conta A→B — ou um logout —
 * deixaria o `em`/`external_id` da conta ANTERIOR grudado no pixel e ele sairia em todo evento. Limpamos só os campos
 * que EXISTEM (tolera versões do SDK com campos a mais/menos), no MESMO objeto de `getState().pixels`
 * (= `instance.pixelsByID`). Depois disto, um novo `init` (conta nova) repovoa o userData; sem `init` (logout), o
 * pixel segue registrado porém vazio → visitante sem matching. Idempotente; sem o SDK carregado, não faz nada.
 */
function resetarUserDataPixel(w: W): void {
  try {
    const pixels = w.fbq?.getState?.()?.pixels;
    if (!Array.isArray(pixels)) return;
    for (const p of pixels) {
      if (p?.id == null || String(p.id) !== PIXEL_ID) continue;
      for (const campo of CAMPOS_USERDATA) {
        const atual = p[campo];
        if (Array.isArray(atual)) p[campo] = [];
        else if (atual && typeof atual === 'object') p[campo] = {};
      }
    }
  } catch {
    // getState ausente/instável: nada a limpar (ainda não há pixel registrado no SDK)
  }
}

/** O objeto do pixel 01 na lista viva do SDK (o MESMO que init/trackSingle leem), ou undefined se o SDK não está pronto. */
function pixelAtual(w: W): PixelSdk | undefined {
  try {
    const pixels = w.fbq?.getState?.()?.pixels;
    if (!Array.isArray(pixels)) return undefined;
    return pixels.find(p => p?.id != null && String(p.id) === PIXEL_ID);
  } catch {
    return undefined;
  }
}

/** O `em` (minúsculo) que o SDK realmente guardou no userData do pixel, ou null — leitura de volta antes de enviar. */
function emDoPixel(px: PixelSdk): string | null {
  const em = (px.userData as { em?: unknown } | undefined)?.em;
  return typeof em === 'string' ? em.trim().toLowerCase() : null;
}

// Geração ativa: cada start captura a sua; listeners globais de instâncias anteriores viram no-op (ex.: testes).
let geracaoAtiva = 0;

export interface TynkPixelOpts {
  hosts?: readonly string[];
  /** Rotas a mais para pular. Só soma: não tira nenhuma de `ROTAS_FORA`. */
  skip?: (path: string) => boolean;
  /** Carrega o GTM pelo `/tk` do host. Padrão: sim. */
  gtm?: boolean;
  timeoutMs?: number;
  limitePrivadaMs?: number;
  limiteDestravadoMs?: number;
  /** Gancho de espera do carregamento do fbevents (teste). Padrão: onload do <script>. */
  aguardarScript?: (el: HTMLScriptElement | null) => Promise<void>;
}

/**
 * Liga o pixel. O stub + init só nascem quando um PageView autorizado vai sair (telas públicas nunca baixam o
 * fbevents). O trackSingle só é chamado com a lib DESTRAVADA, reconferindo tudo de forma síncrona antes de enviar.
 */
export function startTynkPixel(opts: TynkPixelOpts = {}): void {
  if (typeof window === 'undefined') return;
  const w = window as W;
  const hosts = opts.hosts ?? HOSTS_PADRAO;
  if (typeof BUILD_CONFIG !== 'undefined' && BUILD_CONFIG.isElectron) return;
  if (
    !podeAtivar({ hostname: location.hostname, isTop: w.top === w, hosts }) ||
    w.__tynkPixel
  )
    return;
  w.__tynkPixel = true;

  const minhaGeracao = ++geracaoAtiva;
  const ativo = (): boolean => minhaGeracao === geracaoAtiva;
  const skip = (p: string): boolean =>
    rotaDeveSerIgnorada(p) || !!opts.skip?.(p);
  // O `replaceState` de antes do nosso patch (lá embaixo): a limpeza passa por ele e não gera medição a mais.
  const replaceOriginal = history.replaceState.bind(history);
  const aguardarScript = opts.aguardarScript ?? aguardarScriptPadrao;
  const timeoutMs = opts.timeoutMs ?? TIMEOUT_MS;
  const limitePrivadaMs = opts.limitePrivadaMs ?? LIMITE_PRIVADA_MS;
  const limiteDestravadoMs = opts.limiteDestravadoMs ?? LIMITE_DESTRAVADO_MS;

  // Identidade (mutável; reconferida a cada navegação). Guardamos a claim `ent` crua (não o tipo já computado):
  // o `user_type` é recalculado no instante do envio com a data de hoje, então uma assinatura que vence na virada
  // do dia deixa de mandar `member`. `travado` fica só para falha dura (referrer não saneado): logout e troca de
  // conta NÃO travam mais — reiniciam com a nova identidade (zera o userData de A e re-init de B), decisão Jeff 26/10.
  let logado = false;
  let emailAtual: string | undefined;
  let entAtual: unknown;
  const dados: Record<string, string> = {};
  let travado = false;

  // Pixel/script e referrer (neutralizado antes de injetar o fbevents).
  let pixelInit = false;
  // `em` (minúsculo) que o SDK REALMENTE carrega no userData do pixel (undefined = vazio/visitante). Confirmado por
  // read-back em `conciliarSdk`; é o que o envio compara com a conta atual (fail-closed se não baterem).
  let emNoSdk: string | undefined;
  let scriptPromise: Promise<void> | null = null;
  let fbq: FbqStub | undefined;
  let referrerOk = false;
  let referrerFalhou = false;

  // Navegação: id monotônico invalida decisões pendentes antigas (#A→B→A). Rajadas de pushState+replaceState na
  // MESMA URL colapsam por este id (a decisão anterior vira obsoleta — `meuNav !== navAtual`). O dedupe fino fica no
  // ENVIO, por (rota + identidade): assim um login/troca de conta na mesma URL ainda rende UM PageView com a nova
  // identidade (decisão Jeff 26/10), enquanto um re-render na mesma rota e identidade não duplica.
  let navAtual = 0;
  let ultimoEnvio = '';
  let ultimaRota = ''; // a rota da última medição: o dedupe vale só entre navegações consecutivas (A→B→A reconta)

  type StatusVerif = 'ok' | 'indisponivel';
  // Reconfere a sessão do AFFiNE (barato) e re-resolve a claim `ent` a CADA navegação — mesmo com o e-mail
  // inalterado — para refletir revogação/recuperação da assinatura (o `user_type` segue sendo computado no envio).
  // Só o external_id/hash é reaproveitado quando o e-mail não muda. Resultado vinculado ao navId: uma navegação
  // superada (outra começou durante o fetch) não muda identidade, não re-inicializa nem trava.
  const executarVerificacao = async (meuNav: number): Promise<StatusVerif> => {
    if (travado) return 'ok';
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const sess = await buscarSessaoApp(ctrl.signal);
      if (meuNav !== navAtual) return 'ok';
      if (sess.estado === 'indisponivel') return 'indisponivel';
      const novoEmail = sess.estado === 'logado' ? sess.email : undefined;
      if (!novoEmail) {
        // Logout (decisão Jeff 26/10): segue como VISITANTE sem herdar nada da conta anterior. Só o estado do MÓDULO
        // muda aqui; o reset do userData no SDK (e o re-init) acontece pós-destrave em `conciliarSdk` (achado 1),
        // nunca sob o lock — onde um init ficaria enfileirado e repovoaria um userData já limpo.
        emailAtual = undefined;
        logado = false;
        entAtual = undefined;
        delete dados.em;
        delete dados.external_id;
        return 'ok';
      }
      // Logado. Resolve a claim ANTES de qualquer commit: INDISPONÍVEL (rede/abort/não-2xx) NÃO vira lead — pula o
      // evento desta navegação (não baixa/init/envia) e a próxima reenvia (decisão Jeff 26/10, achado 3).
      const mesmoEmail = novoEmail === emailAtual;
      const entRes = await buscarEnt(novoEmail, ctrl.signal);
      if (meuNav !== navAtual) return 'ok';
      if (entRes.estado === 'indisponivel') return 'indisponivel';
      if (!mesmoEmail) {
        // Hash do e-mail da conta ATUAL. Qualquer resultado de SHA-256 de uma navegação superada é descartado pelo
        // guard de navId abaixo; só comita o e-mail/hash DEPOIS do hash e do guard, de modo que uma navegação superada
        // no meio da busca não deixe `emailAtual` salvo sem o resto da identidade — nunca vaza `em`/`external_id` de
        // outra conta (decisão Jeff 26/10). A troca/reinício no SDK fica para `conciliarSdk` (pós-destrave).
        const externalId = await sha256Hex(novoEmail);
        if (meuNav !== navAtual) return 'ok';
        emailAtual = novoEmail;
        dados.em = novoEmail;
        dados.external_id = externalId;
      }
      logado = true;
      // Sempre reflete a claim atual: assinatura revogada volta a `lead`; recuperada volta a `member` (o `user_type` é
      // recomputado no envio com a data de hoje).
      entAtual = entRes.ent;
      return 'ok';
    } catch {
      return 'ok';
    } finally {
      clearTimeout(timer);
    }
  };

  // Serializa as verificações: cada navegação faz a SUA (nunca reusa uma de navegação anterior ainda em voo).
  let cadeia: Promise<StatusVerif> = Promise.resolve('ok');
  const verificarIdentidade = (meuNav: number): Promise<StatusVerif> => {
    const p = cadeia.then(() => executarVerificacao(meuNav));
    cadeia = p.then(
      () => 'ok' as const,
      () => 'ok' as const
    );
    return p;
  };

  // Sanea o referrer antes de injetar o fbevents; se o override não "colar", falha fechado (trava + não baixa).
  const garantirReferrerSaneado = (): boolean => {
    if (referrerOk) return true;
    if (referrerFalhou) return false;
    try {
      const saneado = sanitizarReferrer(
        typeof document !== 'undefined' ? document.referrer : '',
        hosts,
        skip
      );
      Object.defineProperty(document, 'referrer', {
        configurable: true,
        get: () => saneado,
      });
      if (document.referrer !== saneado) {
        referrerFalhou = true;
        return false;
      }
      referrerOk = true;
      return true;
    } catch {
      referrerFalhou = true;
      return false;
    }
  };

  const garantirScriptCarregado = (): Promise<void> => {
    if (!scriptPromise) {
      if (!garantirReferrerSaneado()) {
        travado = true;
        return Promise.reject(new Error('referrer nao saneado'));
      }
      const el = stub(w);
      fbq = w.fbq;
      if (opts.gtm !== false) carregarGtm(w); // pela mesma porta do pixel, uma vez (`scriptPromise` é single-flight)
      scriptPromise = aguardarScript(el);
    }
    return scriptPromise;
  };

  // UM init (autoConfig=false) com o advanced matching atual. TRAVA a lib (carrega signals/config) → o trackSingle
  // só vem depois do destrave.
  const garantirPixelInit = (): void => {
    if (pixelInit) return;
    pixelInit = true;
    fbq?.('set', 'autoConfig', false, PIXEL_ID);
    // 1º init preenche o userData VAZIO (sem reset; read-back dispensável no vazio). Reinícios de troca/logout ficam
    // para `conciliarSdk`, sempre pós-destrave.
    fbq?.('init', PIXEL_ID, { ...dados });
    isolarPixelDoAutomatico(w); // com o SDK carregado, o pixel nasce no init: isola já, sem depender do poll abaixo
    emNoSdk = logado ? emailAtual : undefined;
    agendarIsolamento(); // desacoplado do envio: isola o pixel 01 assim que o SDK REGISTRAR este init
  };

  // Reconcilia a identidade do SDK com a conta ATUAL, SEMPRE pós-destrave (decisão Jeff 26/10, achado 1): o re-init de
  // uma troca/logout nunca é emitido sob o lock — lá ele ficaria enfileirado e, no destrave, repovoaria um userData que
  // o logout já tinha zerado (mandando o `em` da conta errada no PageView visitor). Zera o userData da conta anterior
  // ANTES do novo init (o SDK só grava userData quando o atual está vazio — nota-sdk.md) e relê o `em` que o SDK
  // realmente guardou (read-back; fail-closed p/ ProtectedDataMode). É síncrono: o hash/external_id já veio resolvido.
  const conciliarSdk = (): void => {
    const alvoEm = logado ? emailAtual : undefined;
    if (alvoEm === emNoSdk) return; // o SDK já carrega a identidade atual
    if (emNoSdk !== undefined) resetarUserDataPixel(w); // havia conta anterior: zera antes do novo init
    fbq?.(
      'init',
      PIXEL_ID,
      alvoEm ? { em: dados.em, external_id: dados.external_id } : {}
    );
    const px = pixelAtual(w);
    // Sem getState (SDK não expõe a lista, ex.: teste rápido) assume que o init pegou; com getState, confia SÓ no
    // read-back — se o SDK não assumiu a nova identidade, `emNoSdk` não bate com a conta atual e o envio bloqueia.
    emNoSdk = px ? (emDoPixel(px) ?? undefined) : alvoEm;
  };

  // A lib está destravada agora? (sinal do fbevents.js: fbq.instance.locks.isLocked())
  const estaDestravado = (): boolean => {
    const locks = w.fbq?.instance?.locks;
    if (typeof locks?.isLocked !== 'function') return false;
    try {
      return !locks.isLocked();
    } catch {
      return false;
    }
  };

  // Poll genérico: resolve `true` no 1º `pronto()` ENQUANTO `valido()`; `false` se `valido()` cair ou estourar
  // `limiteMs` (fail-closed, nunca enfileira). `aoRegistrar` liga um gancho opcional (ex.: onUnlocked) que também
  // dispara a checagem. O envio passa `() => meuNav === navAtual` (desiste se outra navegação começa); o isolamento
  // desacoplado passa `ativo` (só desiste se um start mais novo substitui esta instância).
  const aguardarCondicao = (
    pronto: () => boolean,
    valido: () => boolean,
    limiteMs: number,
    aoRegistrar?: (checar: () => void) => void
  ): Promise<boolean> =>
    new Promise(resolve => {
      if (pronto()) return resolve(true);
      if (!valido()) return resolve(false);
      let feito = false;
      const fim = (v: boolean): void => {
        if (feito) return;
        feito = true;
        clearTimeout(timer);
        clearInterval(poll);
        resolve(v);
      };
      const checar = (): void => {
        if (!valido()) fim(false);
        else if (pronto()) fim(true);
      };
      const timer = setTimeout(() => fim(false), limiteMs);
      const poll = setInterval(checar, 25);
      try {
        aoRegistrar?.(checar);
      } catch {
        // gancho opcional; o polling cobre
      }
    });

  // Espera o destrave (fbq.instance.locks.isLocked()===false), com o gancho onUnlocked além do polling.
  const aguardarDestravado = (
    valido: () => boolean,
    limiteMs: number
  ): Promise<boolean> =>
    aguardarCondicao(estaDestravado, valido, limiteMs, checar =>
      w.fbq?.instance?.locks?.onUnlocked?.(checar)
    );

  // Espera o SDK REGISTRAR o pixel 01 — o que acontece quando o init é PROCESSADO, ANTES de destravar e drenar a fila.
  // É aqui (e não no destrave) que o `trackSingleOnly` precisa entrar: um PageView automático enfileirado na config
  // (ex.: restauração de bfcache com outro pixel ativo) drena NO destrave, e o laço global só pula o pixel 01 se a
  // marca já estiver aplicada (decisão Jeff t49s691, achado 4). Sem getState não há nada a isolar → resolve na hora.
  const aguardarPixelRegistrado = (
    valido: () => boolean,
    limiteMs: number
  ): Promise<boolean> =>
    aguardarCondicao(
      () =>
        pixelAtual(w) !== undefined || typeof w.fbq?.getState !== 'function',
      valido,
      limiteMs
    );

  // Isolamento DESACOPLADO do envio (decisão do Jeff t49s691): assim que o SDK REGISTRA o pixel 01 (no init, antes de
  // destravar/drenar a fila), aplica o `trackSingleOnly` no objeto do pixel MESMO que o envio seja cancelado por
  // navegação nova, bloqueado por identidade (logout/troca de conta na carga inicial) ou expire. Isolar na presença do
  // pixel — e não só no destrave — impede que um automático enfileirado na config saia pelo pixel 01 (achado 4).
  // Idempotente, não toca no DOM.
  let isolamentoAgendado = false;
  const agendarIsolamento = (): void => {
    if (isolamentoAgendado) return;
    isolamentoAgendado = true;
    aguardarPixelRegistrado(ativo, limiteDestravadoMs)
      .then(ok => {
        if (ok && ativo()) isolarPixelDoAutomatico(w);
      })
      .catch(() => {
        // aguardarPixelRegistrado nunca rejeita; o catch só satisfaz o lint e nunca deixa o isolamento derrubar a página
      });
  };

  // Reconferência SÍNCRONA de todos os bloqueios, no instante exato de enviar.
  const podeEnviarAgora = (meuNav: number, rota: string): boolean => {
    if (travado || meuNav !== navAtual) return false;
    const p = normalizarCaminho(location.pathname);
    if (p === null || skip(location.pathname)) return false;
    if (temPaginaPublica()) return false;
    if (temParametroSensivel(location.search, location.hash)) return false;
    if (location.pathname + location.search !== rota) return false;
    if (logado && rotaDeEntrada(location.pathname)) return false; // tela de entrada só conta visitante (v10.1)
    if (p.startsWith('/workspace/')) {
      // autorização positiva: só vale o marcador privado DESTE workspace (crachá não serve pra outra sala)
      const prefixo = prefixoWorkspace(p);
      if (!logado || prefixo === null || !temPaginaPrivada(prefixo))
        return false;
    }
    return true;
  };

  const decidir = async (meuNav: number, rotaMedida: string): Promise<void> => {
    let rota = rotaMedida;
    try {
      const status = await verificarIdentidade(meuNav);
      if (status === 'indisponivel' || travado || meuNav !== navAtual) return;
      const p = normalizarCaminho(location.pathname);
      if (p === null) return;
      if (p.startsWith('/workspace/')) {
        const prefixo = prefixoWorkspace(p);
        if (!logado || prefixo === null) return; // visitante/rota inesperada em /workspace não é rastreado
        if (!(await aguardarPaginaPrivada(prefixo, limitePrivadaMs))) return; // espera a confirmação positiva
        if (meuNav !== navAtual || location.pathname + location.search !== rota)
          return;
        // Adaptador: só com o crachá é tela de membro. A SharePage usa o mesmo caminho e a URL do cliente não é nossa,
        // então a limpeza do documento roda aqui, e não em `medir`.
        limparUrl(replaceOriginal);
        rota = location.pathname + location.search;
        ultimaRota = rota;
      }
      // Reconfere ANTES de baixar o script (não baixa em tela pública/sensível), depois faz init, espera o
      // destrave e reconfere de novo — enviando só com a lib destravada e a URL ainda autorizada.
      if (!podeEnviarAgora(meuNav, rota)) return;
      await garantirScriptCarregado();
      if (!podeEnviarAgora(meuNav, rota)) return;
      garantirPixelInit();
      if (
        !(await aguardarDestravado(
          () => meuNav === navAtual,
          limiteDestravadoMs
        ))
      )
        return;
      if (!podeEnviarAgora(meuNav, rota) || !estaDestravado()) return;
      isolarPixelDoAutomatico(w); // belt: o pixel 01 já está destravado no SDK → garante a marca antes do envio
      // Reinicia o SDK com a identidade atual (troca/logout) AGORA que está destravado — síncrono, sem await (o hash já
      // veio resolvido). Faz o reset do userData da conta anterior e o novo init na ordem certa, sem enfileirar sob o
      // lock (achado 1).
      conciliarSdk();
      if (meuNav !== navAtual) return;
      // Fail-closed: só envia se o SDK realmente carrega a identidade da conta ATUAL (read-back confirmado em
      // `conciliarSdk`). Se não assumiu (ex.: ProtectedDataMode), o userData da conta anterior já foi zerado e nada
      // dela vaza (decisão Jeff 26/10, achados 1 e 2).
      const alvoEm = logado ? emailAtual : undefined;
      if (emNoSdk !== alvoEm) return;
      // `user_type` recalculado AGORA (data de hoje): assinatura vencida na virada do dia não manda mais `member`.
      const tipo = tipoDoUsuario({
        logado,
        ent: entAtual,
        email: emailAtual,
      });
      // Dedupe no envio por rota + identidade (e-mail atual + tipo). Mesma rota e mesma identidade não duplica; uma
      // troca de conta / login / logout na MESMA URL muda a chave e rende UM novo PageView (decisão Jeff 26/10).
      const chaveEnvio = `${rota}|${logado ? emailAtual : ''}|${tipo}`;
      if (chaveEnvio === ultimoEnvio) return;
      ultimoEnvio = chaveEnvio;
      const eventId = novoEventId(); // um id por evento, o mesmo no pixel e no dataLayer
      fbq?.(
        'trackSingle',
        PIXEL_ID,
        'PageView',
        { user_type: tipo },
        { eventID: eventId }
      );
      empurrar(w, {
        event: 'page_view',
        event_id: eventId,
        user_type: tipo,
      });
    } catch {
      // nunca deixa uma decisão de PageView derrubar a página
    }
  };

  // `/workspace/<id>` ainda sem o crachá: é uma tela que pode ser o documento público de um cliente.
  const documentoSemCracha = (caminhoNormal: string | null): boolean => {
    if (!caminhoNormal?.startsWith('/workspace/')) return false;
    const prefixo = prefixoWorkspace(caminhoNormal);
    return !prefixo || !temPaginaPrivada(prefixo) || temPaginaPublica();
  };

  const medir = (): void => {
    if (!ativo()) return;
    const caminho = location.pathname;
    const noDocumento = !!normalizarCaminho(caminho)?.startsWith('/workspace/');
    // Limpa ANTES de tudo, menos nas rotas fora. No documento, só depois do crachá (ver `decidir`).
    if (!skip(caminho) && !noDocumento) limparUrl(replaceOriginal);
    const rota = location.pathname + location.search;
    if (rota !== ultimaRota) {
      ultimaRota = rota;
      ultimoEnvio = ''; // A→B(pulada)→A conta como visita nova
    }
    const meuNav = ++navAtual; // cada navegação invalida decisões pendentes antigas; dedupe fino é no envio
    if (skip(caminho)) return; // rota fora: nem defere
    // Preso no search/hash: nem baixa o script. No documento, a mesma pergunta é feita depois do crachá (podeEnviarAgora).
    if (!noDocumento && temParametroSensivel(location.search, location.hash))
      return;
    decidir(meuNav, rota).catch(() => {});
  };

  // A URL nova já ENTRA limpa no history: quem envolve este patch por fora (ex.: um gatilho de histórico do GTM, que
  // carrega depois) lê a URL logo depois da troca e nunca vê o dado sensível. Rota fora, documento sem crachá, outra
  // origem ou URL ilegível passam como vieram.
  const urlLimpa = (alvo: string | URL | null | undefined) => {
    if (alvo === null || alvo === undefined) return alvo;
    try {
      const u = new URL(String(alvo), location.href);
      if (
        u.origin !== location.origin ||
        skip(u.pathname) ||
        documentoSemCracha(normalizarCaminho(u.pathname))
      )
        return alvo;
      return semDadoSensivel(u) ?? alvo;
    } catch {
      return alvo;
    }
  };

  // Troca de rota sem recarregar (React Router): pushState, replaceState e voltar.
  for (const k of ['pushState', 'replaceState'] as const) {
    const orig = history[k].bind(history);
    history[k] = ((
      dados: unknown,
      titulo: string,
      url?: string | URL | null
    ) => {
      orig(dados, titulo, urlLimpa(url));
      queueMicrotask(medir);
    }) as History['pushState'];
  }
  addEventListener('popstate', medir);

  medir();
}
